import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import AttivitaOpportunita from "./AttivitaOpportunita";
import CollegamentiOpportunita from "./CollegamentiOpportunita";
import { api } from "@/lib/api";
import { formatEUR } from "@/lib/utils";
import { fmtData } from "@/lib/listaColonne";
import { VEHICLE_STATUS_LABELS } from "@/lib/labels";
import VehicleStatusBadge from "@/components/VehicleStatusBadge";
import Conflitti, { campiModificati, sovrascrittura } from "@/components/Conflitti";
import { useConfirm } from "@/components/ConfirmProvider";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ExternalLink, Trash2 } from "lucide-react";
import { STATI_OPP, StatoBadge, ContatoreContatto, errore, LINK, TornaIndietro, apriPipeline, fmtDataOra, Pallino } from "./crmComuni";
import { VeicoloDescrittoForm, CercaVeicolo } from "./NuovaOpportunita";
import { INTERAZIONI, RiquadroContatto } from "./Recapiti";
import { CambiaSede, SpostaPipeline } from "./SpostaPipeline";
import { Ferma, Libera, prendiInCarico } from "./CrmOpportunita";

const VOCI = [["dettagli", "Dettagli"], ["note", "Note"], ["attivita", "Attività"], ["storico", "Storico"]];
const formDa = (o) => ({ descrizione: o.descrizione || "", fonte_id: o.fonte_id || null,
  vehicle_id: o.vehicle_id || null, veicolo_descritto: o.veicolo_descritto || null, titolare_id: o.titolare_id || null,
  collaboratori: o.collaboratori || [], url: o.url || "" });

export default function CrmOpportunitaScheda() {
  const { id } = useParams();
  const [o, setO] = useState(null);
  const [sp] = useSearchParams();
  const [voce, setVoce] = useState(() => (["note", "attivita"].includes(sp?.get?.("sezione")) ? sp.get("sezione") : "dettagli"));
  const [errore404, setErrore404] = useState(false);
  const [nNote, setNNote] = useState(0);
  const [giro, setGiro] = useState(0);  // Sprint CRM-2A — dopo un'interazione registrata si ricontano note e interazioni
  useEffect(() => { api.get(`/crm/opportunita/${id}/note`).then((r) => setNNote(r.data.length)).catch(() => setNNote(0)); }, [id, giro]);
  const carica = useCallback(() => api.get(`/crm/opportunita/${id}`).then((r) => setO(r.data)).catch(() => setErrore404(true)), [id]);
  useEffect(() => { carica(); }, [carica]);
  if (errore404) return <Card className="p-8 text-center text-slate-500" data-testid="crm-opp-404">Opportunità non trovata.</Card>;
  if (!o) return <div className="text-slate-500">Caricamento...</div>;
  return (
    <div className="space-y-4" data-testid="crm-opp-page">
      <TornaIndietro to={`/crm?pipeline=${o.pipeline_id}`} testo={`Torna alla pipeline ${o.pipeline?.nome || ""}`} testid="crm-opp-indietro"
        onClick={() => apriPipeline(o.company_id, o.pipeline_id)} />
      <div className="flex gap-4 text-sm text-slate-700" data-testid="crm-opp-date">
        <span>Creata il <b data-testid="crm-opp-creata">{fmtDataOra(o.created_at)}</b></span>
        <span>Ultima modifica il <b data-testid="crm-opp-modificata">{fmtDataOra(o.updated_at)}</b></span>
      </div>
      <AllineamentiFalliti o={o} onRiallineata={setO} />
      <div className="flex items-center gap-3 flex-wrap">
        <h1 className="font-heading text-2xl text-slate-900" data-testid="crm-opp-titolo">{o.descrizione}</h1>
        <StatoBadge stato={o.stato} testid="crm-opp-stato" />
        <span className="text-sm text-slate-600" data-testid="crm-opp-fase">{o.fase?.nome || "—"}</span>
        <Libera o={o} /><Ferma o={o} />
        {o.puo_prendere && <Button size="sm" variant="outline" onClick={() => prendiInCarico(o, carica)} data-testid="crm-opp-prendi">Prendi in carico</Button>}
      </div>
      {/* Sprint CRM-6 — sotto 1024px il menu della scheda va in alto, in riga, e il contenuto sotto */}
      <div className="flex flex-col lg:flex-row gap-4 lg:items-start">
        <nav className="w-full lg:w-44 shrink-0 bg-slate-900 rounded-md p-1 flex lg:flex-col gap-1 overflow-x-auto" data-testid="crm-opp-menu">
          {VOCI.map(([k, l]) => (
            <button key={k} onClick={() => setVoce(k)} data-testid={`crm-opp-menu-${k}`}
              className={`shrink-0 whitespace-nowrap lg:w-full text-left px-3 py-2 rounded text-sm border-l-[3px] transition-colors ${voce === k
                ? "border-sky-400 bg-slate-700 text-white font-semibold" : "border-transparent text-slate-200 hover:bg-slate-800"}`}>
              {l}{k === "note" && nNote > 0 && <span className="ml-2 rounded-full bg-sky-500 text-white text-[11px] px-1.5" data-testid="crm-opp-note-n">{nNote}</span>}
              {k === "attivita" && o.n_attivita > 0 && <span className="ml-2 rounded-full bg-sky-500 text-white text-[11px] px-1.5" data-testid="crm-opp-attivita-n">{o.n_attivita}</span>}</button>
          ))}
        </nav>
        <div className="flex-1 min-w-0">
          {voce === "dettagli" && <div className="space-y-4">
            <Dettagli o={o} onRicarica={carica} setO={setO} onInterazione={() => setGiro((g) => g + 1)} />
            <CollegamentiOpportunita o={o} onCambiata={carica} /></div>}
          {voce === "note" && <Note key={giro} id={id} onConta={setNNote} />}
          {voce === "attivita" && <AttivitaOpportunita o={o} onCambio={carica} />}
          {voce === "storico" && <Storico id={id} />}
        </div>
      </div>
    </div>
  );
}

function FaseStato({ o, onRicarica }) {
  const confirm = useConfirm();
  const [fasi, setFasi] = useState([]);
  const [motivi, setMotivi] = useState([]);
  const [motivo, setMotivo] = useState("");
  useEffect(() => {
    api.get(`/crm/pipeline/${o.pipeline_id}/fasi`).then((r) => setFasi(r.data.filter((f) => !f.archiviata))).catch(() => setFasi([]));
    api.get(`/crm/elenchi?company_id=${o.company_id}&tipo=motivo_perdita`).then((r) => setMotivi(r.data)).catch(() => setMotivi([]));
  }, [o.pipeline_id, o.company_id]);
  const esegui = async (fn) => { try { await fn(); toast.success("Fatto"); } catch (e) { toast.error(errore(e)); } onRicarica(); };
  const spostaFase = async (fid) => {
    const f = fasi.find((x) => x.id === fid);
    if (!f || fid === o.fase_id) return;
    if ((f.tipo !== "normale") !== (o.fase?.tipo !== "normale")) {
      const ok = await confirm({ variant: "approve", title: f.tipo !== "normale" ? "Segna come vinta" : "Rimetti in corso",
        message: f.tipo !== "normale" ? `Spostandola in «${f.nome}» diventa vinta. Confermi?` : `Spostandola in «${f.nome}» torna in corso. Confermi?`,
        confirmLabel: "SI, sposta" });
      if (!ok) return;
    }
    esegui(() => api.post(`/crm/opportunita/${o.id}/fase`, { fase_id: fid, fase_partenza: o.fase_id }));
  };
  const stato = (s, extra = {}) => esegui(() => api.post(`/crm/opportunita/${o.id}/stato`, { stato: s, stato_partenza: o.stato, ...extra }));
  // Sprint CRM-3 — dalla notifica «pratica annullata»: si apre la finestra «Persa» che esiste già; la nota solo alla conferma
  const [spP] = useSearchParams();
  const annullata = spP?.get?.("segna_persa");
  const [persa, setPersa] = useState(() => !!annullata && o.stato !== "persa");
  const confermaPersa = (extra) => esegui(async () => {
    await api.post(`/crm/opportunita/${o.id}/stato`, { stato: "persa", stato_partenza: o.stato, ...extra });
    if (annullata) {
      const [a, m, g] = annullata.split("-");
      await api.post(`/crm/opportunita/${o.id}/note`, { testo: `Contratto annullato il ${g}/${m}/${a}` });
    }
  });
  const scegliStato = async (s) => {
    if (s === o.stato) return;
    if (s === "persa") { setPersa(true); return; }
    if (s === "vinta" && !(await confirm({ variant: "approve", title: "Segna come vinta", confirmLabel: "SI, vinta",
      message: "L'opportunità passa nella prima fase di chiusura della pipeline. Confermi?" }))) return;
    stato(s);
  };
  const chiusa = o.stato === "persa" || o.stato === "abbandonata";
  const [sposta, setSposta] = useState(false);
  return (
    <Card className="p-4 space-y-3" data-testid="crm-opp-fase-stato">
      <div className="flex flex-wrap gap-3 items-end">
        <div><Label className="text-xs">Pipeline</Label>
          <div className="flex items-center gap-2"><span className="text-lg font-semibold text-slate-900" data-testid="crm-opp-pipeline">{o.pipeline?.nome || "—"}</span>
            {o.stato === "aperta" && <Button size="sm" variant="outline" onClick={() => setSposta(true)} data-testid="crm-opp-sposta">Sposta…</Button>}</div></div>
        <CambiaSede o={o} onFatto={onRicarica} />
        {!chiusa && <div><Label className="text-xs">Fase</Label>
          <Select value={o.fase_id} onValueChange={spostaFase}>
            <SelectTrigger className="w-60 max-w-full" data-testid="crm-opp-fase-select"><SelectValue /></SelectTrigger>
            <SelectContent>{fasi.map((f) => <SelectItem key={f.id} value={f.id} disabled={f.tipo === "chiusura_collegata"}>{f.nome}</SelectItem>)}</SelectContent>
          </Select></div>}
        <div><Label className="text-xs">Stato</Label>
          <Select value={o.stato} onValueChange={scegliStato}>
            <SelectTrigger className="w-48 max-w-full" data-testid="crm-opp-stato-select"><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(STATI_OPP).map(([k, l]) => <SelectItem key={k} value={k} data-testid={`crm-opp-stato-${k}`}>{l}</SelectItem>)}</SelectContent>
          </Select></div>
        {persa && <>
          <div><Label className="text-xs">Motivo di perdita (facoltativo)</Label>
            <Select value={motivo || "_"} onValueChange={(v) => setMotivo(v === "_" ? "" : v)}>
              <SelectTrigger className="w-48 max-w-full" data-testid="crm-opp-motivo"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="_">—</SelectItem>{motivi.map((m) => <SelectItem key={m.id} value={m.id}>{m.nome}</SelectItem>)}</SelectContent>
            </Select></div>
          <Button variant="outline" onClick={() => { setPersa(false); confermaPersa({ motivo_perdita_id: motivo || null }); }} data-testid="crm-opp-persa-conferma">Conferma persa</Button>
          <Button variant="ghost" onClick={() => setPersa(false)} data-testid="crm-opp-persa-annulla">Annulla</Button>
        </>}
      </div>
      {o.stato === "persa" && <div className="text-sm text-slate-600" data-testid="crm-opp-motivo-attuale">Motivo: {o.motivo_perdita_nome || "—"}</div>}
      <div className="text-xs text-slate-500" data-testid="crm-opp-giorni">{o.giorni_in_fase == null ? "—" : `${o.giorni_in_fase} giorni nella fase`}</div>
      {sposta && <SpostaPipeline o={o} onChiudi={() => { setSposta(false); onRicarica(); }} />}
    </Card>
  );
}

export function VeicoloAgganciato({ v }) {
  if (!v) return null;
  if (!v.visibile) return <div className="text-sm text-slate-500 italic" data-testid="crm-opp-veicolo-non-visibile">Veicolo agganciato non visibile con i tuoi permessi</div>;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm" data-testid="crm-opp-veicolo">
      <div><span className="text-xs text-slate-500 block">Codice</span><Link className={LINK} to={`/vehicles/${v.id}`} data-testid="crm-opp-veicolo-link">{v.codice || "—"}</Link></div>
      <div><span className="text-xs text-slate-500 block">Marca e modello</span>{[v.marca, v.modello].filter(Boolean).join(" ") || "—"}</div>
      <div><span className="text-xs text-slate-500 block">Versione</span><span data-testid="crm-opp-veicolo-versione">{v.versione || "—"}</span></div>
      <div><span className="text-xs text-slate-500 block">Targa</span>{v.targa || "—"}</div>
      <div><span className="text-xs text-slate-500 block">Stato commerciale</span>{VEHICLE_STATUS_LABELS[v.status] || "—"}{v.nel_cestino ? " · nel cestino" : ""}</div>
      {/* Sprint CRM-2C.1 — disponibilità con la pill del gestionale (VehicleStatusBadge) */}
      <div data-testid="crm-opp-veicolo-disponibilita"><span className="text-xs text-slate-500 block">Disponibilità</span>
        {v.disponibilita_fisica ? <VehicleStatusBadge disponibilita_fisica={v.disponibilita_fisica} size="sm" /> : "—"}</div>
      <div><span className="text-xs text-slate-500 block">Prezzo di Vendita</span><span data-testid="crm-opp-prezzo-vendita">{formatEUR(v.prezzo_vendita)}</span></div>
      <div><span className="text-xs text-slate-500 block">Prezzo WEB</span><span data-testid="crm-opp-prezzo-web">{formatEUR(v.prezzo_web)}</span></div>
    </div>
  );
}

function Dettagli({ o, onRicarica, setO, onInterazione }) {
  const nav = useNavigate();
  const confirm = useConfirm();
  const [form, setForm] = useState(formDa(o));
  const [base, setBase] = useState(formDa(o));
  const [fonti, setFonti] = useState([]);
  const [utenti, setUtenti] = useState([]);
  const [cambiaVeicolo, setCambiaVeicolo] = useState(null);
  const [esito, setEsito] = useState(null);
  const [miei, setMiei] = useState(null);
  const [erroreUrl, setErroreUrl] = useState("");
  useEffect(() => { setForm(formDa(o)); setBase(formDa(o)); }, [o]);
  useEffect(() => {
    api.get(`/crm/elenchi?company_id=${o.company_id}&tipo=fonte`).then((r) => setFonti(r.data)).catch(() => setFonti([]));
    api.get(`/crm/opportunita/${o.id}/utenti-idonei`).then((r) => setUtenti(r.data)).catch(() => setUtenti([]));
  }, [o.id, o.company_id]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const dopoSalvataggio = (r) => {
    setO(r.data);
    if (r.data.conflitti?.length) setEsito(r.data); else toast.success("Salvato");
  };
  const salva = async () => {
    const { payload, originali } = campiModificati(base, form);
    if (!Object.keys(payload).length) { toast.message("Nessuna modifica"); return; }
    setErroreUrl("");
    try { setMiei(payload); dopoSalvataggio(await api.put(`/crm/opportunita/${o.id}`, { ...payload, originali })); }
    catch (e) { if ("url" in payload && errore(e).startsWith("Indirizzo")) setErroreUrl(errore(e)); else toast.error(errore(e)); }
  };
  const applica = async (scelte) => {
    const sovr = esito.conflitti.filter((c) => scelte[c.campo] === "mio");
    setEsito(null);
    if (!sovr.length) return;
    const { payload, originali } = sovrascrittura(sovr, miei);
    try { dopoSalvataggio(await api.put(`/crm/opportunita/${o.id}`, { ...payload, originali })); } catch (e) { toast.error(errore(e)); }
  };
  const elimina = async () => {
    if (!(await confirm({ title: "Elimina opportunità", message: "L'opportunità e le sue note vengono eliminate. Confermi?" }))) return;
    try { await api.delete(`/crm/opportunita/${o.id}`); toast.success("Eliminata"); nav(`/crm?pipeline=${o.pipeline_id}`); }
    catch (e) { toast.error(errore(e)); }
  };
  const tog = (uid) => set("collaboratori", form.collaboratori.includes(uid) ? form.collaboratori.filter((x) => x !== uid) : [...form.collaboratori, uid]);
  const descritto = !form.vehicle_id && cambiaVeicolo !== "archivio";
  // Sprint CRM-1.1 — sostituire o togliere un veicolo già agganciato chiede conferma; Annulla = nessuna modifica
  const confermaVeicolo = async (togli) => !form.vehicle_id || confirm({ variant: "approve",
    title: togli ? "Togli il veicolo" : "Sostituisci il veicolo", confirmLabel: togli ? "SI, togli" : "SI, sostituisci",
    message: `Stai ${togli ? "togliendo" : "sostituendo"} il veicolo ${(form.vehicle_id === o.vehicle_id && o.veicolo?.codice) || "agganciato"}. Il precedente resta nello storico. Confermi?` });
  const scegliVeicolo = async (v) => {
    if (!(await confermaVeicolo(false))) return;
    set("vehicle_id", v.id); set("veicolo_descritto", null); setCambiaVeicolo(null);
  };
  const descrivi = async () => {
    if (!(await confermaVeicolo(true))) return;
    setCambiaVeicolo("descritto"); set("vehicle_id", null); set("veicolo_descritto", form.veicolo_descritto || {});
  };
  const togli = async () => { if (await confermaVeicolo(true)) { set("vehicle_id", null); setCambiaVeicolo(null); } };
  return (
    <div className="space-y-4">
      <FaseStato o={o} onRicarica={onRicarica} />
      <Card className="p-4 space-y-3" data-testid="crm-opp-dettagli">
        <div><Label>Descrizione</Label><Input value={form.descrizione} onChange={(e) => set("descrizione", e.target.value)} data-testid="crm-opp-descrizione" /></div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><Label>Contatto</Label>
            <RiquadroContatto c={o.contatto} opportunita={[o]} onRegistrata={onInterazione} prefix="crm-opp-contatto"
              titolo={o.contatto && <Link className={LINK} to={`/crm/contatti/${o.contatto.id}`} data-testid="crm-opp-contatto-link">{o.contatto.nome}</Link>} />
            <ContatoreContatto conteggi={o.contatto?.conteggi} testid="crm-opp-contatore" /></div>
          <div><Label>Fonte</Label>
            <Select value={form.fonte_id || "_"} onValueChange={(v) => set("fonte_id", v === "_" ? null : v)}>
              <SelectTrigger data-testid="crm-opp-fonte"><SelectValue placeholder={o.fonte_nome || "—"} /></SelectTrigger>
              <SelectContent><SelectItem value="_">—</SelectItem>
                {o.fonte_id && !fonti.some((f) => f.id === o.fonte_id) && <SelectItem value={o.fonte_id} disabled>{o.fonte_nome} (archiviata)</SelectItem>}
                {fonti.map((f) => <SelectItem key={f.id} value={f.id}>{f.nome}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label>Titolare</Label>
            {o.puo?.titolare ? (
              <Select value={form.titolare_id || "_"} onValueChange={(v) => set("titolare_id", v === "_" ? null : v)}>
                <SelectTrigger data-testid="crm-opp-titolare"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="_">— nessuno —</SelectItem>{utenti.map((u) => <SelectItem key={u.id} value={u.id}>{u.nome}</SelectItem>)}</SelectContent>
              </Select>) : <div className="text-sm flex items-center gap-2" data-testid="crm-opp-titolare-testo">
                <Pallino id={o.titolare_id} nome={o.titolare_nome} testid="crm-opp-pallino" />{o.titolare_nome || "—"}</div>}</div>
          <div><Label>Collaboratori</Label>
            {o.puo?.collaboratori ? (
              <div className="flex flex-wrap gap-1" data-testid="crm-opp-collaboratori">
                {utenti.filter((u) => u.id !== form.titolare_id).map((u) => (
                  <button key={u.id} type="button" onClick={() => tog(u.id)} data-testid={`crm-opp-collab-${u.id}`}
                    className={`px-2 py-0.5 rounded border text-xs ${form.collaboratori.includes(u.id) ? "bg-sky-100 border-sky-400" : "border-slate-200"}`}>{u.nome}</button>
                ))}</div>) : <div className="text-sm">{o.collaboratori_nomi?.join(", ") || "—"}</div>}</div>
        </div>
        <div><Label>URL</Label>
          <div className="flex items-center gap-3">
            <Input value={form.url} onChange={(e) => { set("url", e.target.value); setErroreUrl(""); }} placeholder="https://…"
              className={erroreUrl ? "border-rose-500" : ""} data-testid="crm-opp-url" />
            {o.url && <a href={o.url} target="_blank" rel="noopener noreferrer" className={`${LINK} inline-flex items-center gap-1 text-sm whitespace-nowrap`}
              data-testid="crm-opp-url-link"><ExternalLink className="h-4 w-4" /> Apri l'indirizzo</a>}
          </div>
          {erroreUrl && <div className="text-xs text-rose-600 mt-1" data-testid="crm-opp-url-errore">{erroreUrl}</div>}</div>
        <div className="border-t pt-3 space-y-2">
          <div className="flex flex-wrap items-center gap-2"><Label>Veicolo</Label>
            <Button size="sm" variant="outline" onClick={() => setCambiaVeicolo("archivio")} data-testid="crm-opp-aggancia">Aggancia dall'archivio</Button>
            <Button size="sm" variant="outline" onClick={descrivi} data-testid="crm-opp-descrivi">Descrivi il veicolo</Button>
            {form.vehicle_id && <Button size="sm" variant="outline" className="text-rose-600" onClick={togli} data-testid="crm-opp-togli-veicolo">Togli veicolo</Button>}</div>
          {cambiaVeicolo === "archivio" && <CercaVeicolo companyId={o.company_id} prefix="crm-opp"
            onScegli={scegliVeicolo} />}
          {form.vehicle_id && form.vehicle_id === o.vehicle_id && <VeicoloAgganciato v={o.veicolo} />}
          {form.vehicle_id && form.vehicle_id !== o.vehicle_id && <div className="text-sm text-amber-700" data-testid="crm-opp-veicolo-nuovo">Nuovo veicolo scelto: salva per agganciarlo.</div>}
          {descritto && form.veicolo_descritto && <VeicoloDescrittoForm vd={form.veicolo_descritto} setVd={(v) => set("veicolo_descritto", v)} prefix="crm-opp" />}
          {!form.vehicle_id && !form.veicolo_descritto && <div className="text-sm text-slate-400">—</div>}
        </div>
        <div className="flex justify-between pt-2">
          {o.puo?.elimina ? <Button variant="outline" className="text-rose-600" onClick={elimina} data-testid="crm-opp-elimina"><Trash2 className="h-4 w-4 mr-1" /> Elimina</Button> : <span />}
          <Button onClick={salva} className="bg-[#0F4C81] hover:bg-[#0c3d68]" data-testid="crm-opp-salva">Salva</Button>
        </div>
      </Card>
      <Conflitti esito={esito} mieiValori={miei} onApplica={applica}
        onChiudi={() => { setEsito(null); toast.message("Tenuti i valori attuali sui campi in conflitto"); }} />
    </div>
  );
}

function Note({ id, onConta }) {
  const [note, setNote] = useState([]);
  const [testo, setTesto] = useState("");
  const carica = useCallback(() => api.get(`/crm/opportunita/${id}/note`).then((r) => { setNote(r.data); onConta(r.data.length); })
    .catch(() => setNote([])), [id, onConta]);
  useEffect(() => { carica(); }, [carica]);
  const aggiungi = async () => {
    try { await api.post(`/crm/opportunita/${id}/note`, { testo }); setTesto(""); carica(); } catch (e) { toast.error(errore(e)); }
  };
  return (
    <Card className="p-4 space-y-3" data-testid="crm-opp-note">
      <Textarea value={testo} onChange={(e) => setTesto(e.target.value)} placeholder="Scrivi una nota" data-testid="crm-nota-testo" />
      <Button onClick={aggiungi} disabled={!testo.trim()} data-testid="crm-nota-aggiungi">Aggiungi nota</Button>
      {note.map((n) => <VoceNota key={n.id} n={n} oid={id} onCambio={carica} />)}
      {!note.length && <div className="text-sm text-slate-400">Nessuna nota</div>}
    </Card>
  );
}

// Sprint CRM-2A — note e interazioni insieme, in ordine di data, ognuna con icona, autore e data
// Sprint CRM-5 — l'autore modifica e cancella le proprie note; delle interazioni registrate corregge solo l'esito
export function VoceNota({ n, extra, oid, onCambio }) {
  const confirm = useConfirm();
  const [testo, setTesto] = useState(null);
  const t = INTERAZIONI[n.tipo] || INTERAZIONI.nota;
  const I = t.icon;
  const nota = n.tipo === "nota";
  const salva = async () => {
    try { await api.put(`/crm/opportunita/${oid}/note/${n.id}`, { testo }); setTesto(null); onCambio(); } catch (e) { toast.error(errore(e)); }
  };
  const cancella = async () => {
    if (!(await confirm({ title: "Cancella nota", message: "La nota sparisce dalla scheda; il testo resta nello storico. Confermi?", confirmLabel: "SI, cancella" }))) return;
    try { await api.delete(`/crm/opportunita/${oid}/note/${n.id}`); onCambio(); } catch (e) { toast.error(errore(e)); }
  };
  return (
    <div className="border-t pt-2 text-sm flex gap-2" data-testid={`crm-nota-${n.id}`}>
      <I className="h-4 w-4 mt-0.5 text-[#0F4C81] shrink-0" data-testid={`crm-nota-icona-${n.id}`} />
      <div className="min-w-0 flex-1">
        <div className="text-xs text-slate-500">{t.label} · {fmtData(n.data)} {new Date(n.data).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })} · {n.autore_nome || "—"}{extra}
          {n.modificata_il && <span data-testid={`crm-nota-modificata-${n.id}`}> · modificata il {fmtData(n.modificata_il)} da {n.modificata_da_nome || "—"}</span>}</div>
        {testo === null ? n.testo && <div className="whitespace-pre-wrap">{n.testo}</div> : (
          <div className="space-y-1 mt-1">
            <Textarea value={testo} onChange={(e) => setTesto(e.target.value)} placeholder={nota ? "" : "Esito (facoltativo)"} data-testid={`crm-nota-modifica-testo-${n.id}`} />
            <div className="flex gap-2"><Button size="sm" onClick={salva} disabled={nota && !testo.trim()} data-testid={`crm-nota-salva-${n.id}`}>Salva</Button>
              <Button size="sm" variant="outline" onClick={() => setTesto(null)} data-testid={`crm-nota-annulla-${n.id}`}>Annulla</Button></div>
          </div>)}
      </div>
      {/* Sprint CRM-6 — comandi solo con la casella giusta (propria/altrui, modifica/elimina) */}
      {onCambio && testo === null && (n.puo_modifica || (nota && n.puo_elimina)) && <div className="flex gap-2 shrink-0 text-xs">
        {n.puo_modifica && <button type="button" className="text-blue-600 hover:underline" onClick={() => setTesto(n.testo || "")} data-testid={`crm-nota-modifica-${n.id}`}>
          {nota ? "Modifica" : "Modifica esito"}</button>}
        {nota && n.puo_elimina && <button type="button" className="text-rose-600 hover:underline" onClick={cancella} data-testid={`crm-nota-cancella-${n.id}`}>Cancella</button>}
      </div>}
    </div>
  );
}

const AZIONI = { "crm_opportunita.create": "Creata", "crm_opportunita.update": "Modificata", "crm_opportunita.fase": "Cambio di fase",
  "crm_opportunita.stato": "Cambio di stato", "crm_opportunita.nota": "Nota aggiunta",
  "crm_opportunita.interazione": "Interazione registrata", "crm_opportunita.presa_in_carico": "Presa in carico",
  "crm_opportunita.pipeline": "Spostata in un'altra pipeline",
  "crm_opportunita.nota_modificata": "Nota o esito modificato", "crm_opportunita.nota_cancellata": "Nota cancellata",
  "crm.allineamento_fallito": "Allineamento con la pratica non riuscito", "crm.allineamento_riuscito": "Riallineata con la pratica" };

const fmtGiorno = (iso) => new Date(iso).toLocaleDateString("it-IT", { timeZone: "Europe/Rome", day: "2-digit", month: "2-digit", year: "numeric" });

// Sprint CRM-7 — allineamento pratica → CRM non riuscito: riga ben visibile e «Riallinea» (visibili a chi vede l'opportunità)
export function AllineamentiFalliti({ o, onRiallineata }) {
  const [inCorso, setInCorso] = useState(null);
  const riallinea = async (vid) => {
    setInCorso(vid);
    try { onRiallineata((await api.post(`/crm/opportunita/${o.id}/riallinea`, { vendita_id: vid })).data); toast.success("Riallineata con la pratica"); }
    catch (e) { toast.error(errore(e)); }
    finally { setInCorso(null); }
  };
  if (!o.allineamenti_falliti?.length) return null;
  return o.allineamenti_falliti.map((p) => (
    <div key={p.vendita_id} className="flex flex-wrap items-center gap-3 rounded-md border border-rose-300 bg-rose-50 px-4 py-3 text-sm text-rose-800"
      data-testid={`crm-allineamento-fallito-${p.vendita_id}`}>
      <span className="font-medium">Allineamento con la pratica {p.codice} non riuscito il {fmtGiorno(p.data)}</span>
      <Button size="sm" variant="outline" className="ml-auto" disabled={inCorso === p.vendita_id} onClick={() => riallinea(p.vendita_id)}
        data-testid={`crm-riallinea-${p.vendita_id}`}>Riallinea</Button>
    </div>));
}
const CAMPI = { fase_id: "Fase", stato: "Stato", titolare_id: "Titolare", collaboratori: "Collaboratori", fonte_id: "Fonte",
  motivo_perdita_id: "Motivo di perdita", vehicle_id: "Veicolo agganciato", descrizione: "Descrizione", veicolo_descritto: "Veicolo descritto",
  url: "URL", contatto_id: "Contatto", pipeline_id: "Pipeline", site_id: "Sede", tutte_le_sedi: "Tutte le sedi" };

function Storico({ id }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/crm/opportunita/${id}/storico`).then((r) => setD(r.data)).catch(() => setD({ voci: [], nomi: {} })); }, [id]);
  if (!d) return null;
  const val = (x) => (x == null || x === "" ? "—" : typeof x === "object" ? JSON.stringify(x) : d.nomi[x] || String(x));
  return (
    <Card className="p-4 space-y-2" data-testid="crm-opp-storico">
      {d.voci.map((v, i) => (
        <div key={i} className="border-b pb-2 text-sm" data-testid={`crm-storico-${i}`}>
          <div className="text-xs text-slate-500">{fmtData(v.timestamp)} {new Date(v.timestamp).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })} · {v.autore || "—"}</div>
          <div className="font-medium">{AZIONI[v.action] || v.action}</div>
          {"testo_precedente" in v && <div className="text-xs text-slate-700 whitespace-pre-wrap" data-testid={`crm-storico-testo-${i}`}>
            Prima: {v.testo_precedente || "—"}{v.action === "crm_opportunita.nota_modificata" ? ` → Dopo: ${v.testo || "—"}` : ""}</div>}
          {Object.entries(v.changes).map(([k, c]) => (
            <div key={k} className="text-xs text-slate-700">{CAMPI[k] || k}: {c._type === "array_scalar_diff"
              ? `${c.added.length ? `+ ${c.added.map(val).join(", ")}` : ""} ${c.removed.length ? `− ${c.removed.map(val).join(", ")}` : ""}`
              : k === "stato" ? `${STATI_OPP[c.from] || "—"} → ${STATI_OPP[c.to] || "—"}` : `${val(c.from)} → ${val(c.to)}`}</div>
          ))}
        </div>
      ))}
      {!d.voci.length && <div className="text-sm text-slate-400">Nessuna voce</div>}
    </Card>
  );
}
